// Keeping the address, the view store, the document language/theme and i18next in step.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { usePrefersDark } from '../lib/media';
import { useViewStore } from '../state/store';
import { readUrlState, writeUrlState } from '../state/url';
import { useView } from './view';
import type { AuthMe } from '../api/types';

/** Saved account defaults on entry; an explicit shared-link language/theme wins for that entry only. */
export function AccountDisplaySync({ me }: { me: AuthMe }) {
  const location = useLocation();
  const previous = useRef<{ userId: string; lang: string; theme: string } | null>(null);
  useLayoutEffect(() => {
    const before = previous.current;
    const link = readUrlState(location.search);
    const first = before?.userId !== me.user.id;
    useViewStore.getState().apply({
      ...(first ? { lang: link.lang ?? me.prefs.lang } : before.lang !== me.prefs.lang ? { lang: me.prefs.lang } : {}),
      ...(first ? { theme: link.theme ?? me.prefs.theme } : before.theme !== me.prefs.theme ? { theme: me.prefs.theme } : {}),
    });
    previous.current = { userId: me.user.id, lang: me.prefs.lang, theme: me.prefs.theme };
  }, [me.user.id, me.prefs.lang, me.prefs.theme, location.search]);
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--aiax-font-scale', String({ m: 1, l: 8 / 7, xl: 9 / 7 }[me.prefs.fontSize]));
    return () => { root.style.removeProperty('--aiax-font-scale'); };
  }, [me.prefs.fontSize]);
  return null;
}

/** Theme on <html data-theme> (light/dark; system = no attribute → CSS follows the OS) and language on <html lang> + i18next. */
export function DocumentSync() {
  const theme = useViewStore((s) => s.theme);
  const lang = useViewStore((s) => s.lang);
  const { i18n } = useTranslation();
  usePrefersDark(); // re-render when the OS theme changes (theme buttons rename themselves)
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme);
    else root.removeAttribute('data-theme');
  }, [theme]);
  useLayoutEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
  }, [lang, i18n]);
  return null;
}

/** Is the dark theme showing now (chosen dark, or system + OS dark)? */
export function useIsDark(): boolean {
  const theme = useViewStore((s) => s.theme);
  const osDark = usePrefersDark();
  return theme === 'dark' || (theme === 'system' && osDark);
}

/**
 * Address ↔ view store, inside the signed-in shell (mockup syncUrl):
 *  · back/forward (POP) applies the state keys found in the address;
 *  · every state change rewrites the address (replace) with lang · theme · scope · period (+ method · team · person),
 *    keeping the screen's own keys (item, tab, page …). Links without state keys therefore keep the current view.
 * The first address was already read when the store was created; the effective scope/team for this person and screen
 * is what gets written (a link someone shared is re-judged with the reader's permissions).
 */
export function useUrlStateSync(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const view = useView();
  const lang = useViewStore((s) => s.lang);
  const theme = useViewStore((s) => s.theme);
  const apply = useViewStore((s) => s.apply);
  const first = useRef(true);

  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (navType !== 'POP') return;
    const u = readUrlState(location.search);
    apply({
      ...(u.lang ? { lang: u.lang } : {}),
      ...(u.theme ? { theme: u.theme } : {}),
      ...(u.scope ? { scopeWanted: u.scope } : {}),
      ...(u.period ? { period: u.period } : {}),
      ...(u.method ? { method: u.method } : {}),
      team: u.team ?? null,
      person: u.person ?? null,
    });
    // only on history moves
  }, [location.key]);

  useEffect(() => {
    const next = writeUrlState(location.search, { lang, theme, scope: view.scope, period: view.period, method: view.method, team: view.team, person: view.person });
    if (next !== location.search) navigate({ pathname: location.pathname, search: next, hash: location.hash }, { replace: true, preventScrollReset: true });
  }, [lang, theme, view.scope, view.period, view.method, view.team, view.person, location.pathname, location.search, location.hash, navigate]);
}
